// The booth controls (bottom right, clubs only): go live from your mic or
// a tab's audio, see that you're on, end the set. Renders from the
// Broadcaster's state machine + the room's live flag every frame.

import type { Broadcaster } from "../audio/broadcast";

export class BoothControls {
  private root: HTMLDivElement;
  private clubId = "";
  private lastKey = "";

  constructor(
    parent: HTMLElement,
    private broadcaster: Broadcaster,
  ) {
    this.root = document.createElement("div");
    this.root.className = "booth-controls";
    this.root.hidden = true;
    parent.append(this.root);

    this.root.addEventListener("click", (e) => {
      const action = (e.target as HTMLElement).dataset.action;
      if (!action) return;
      if (action === "open") this.render("choose");
      else if (action === "cancel" || action === "dismiss") {
        this.broadcaster.clearError();
        this.render("idle");
      } else if (action === "mic" || action === "tab") {
        void this.broadcaster.start(this.clubId, action);
      } else if (action === "stop") {
        this.broadcaster.stop();
      }
    });

    broadcaster.onChange = () => this.update(this.clubId, this.someoneElseLive);
  }

  private someoneElseLive = false;

  /** Called every frame. clubId empty = not in a club (hide everything). */
  update(clubId: string, roomLive: boolean): void {
    this.clubId = clubId;
    const state = this.broadcaster.state;
    // "Live" from the room state is someone else only when it isn't us.
    this.someoneElseLive = roomLive && state !== "live" && state !== "connecting";

    if (!clubId) {
      this.root.hidden = true;
      this.lastKey = "";
      return;
    }
    this.root.hidden = false;

    if (state === "live") this.render(this.broadcaster.sourceSilent() ? "live-silent" : "live");
    else if (state === "connecting") this.render("connecting");
    else if (state === "error") this.render("error");
    else if (this.someoneElseLive) this.render("occupied");
    else if (this.lastKey !== "choose") this.render("idle");
  }

  private render(key: string): void {
    if (this.lastKey === key) return;
    this.lastKey = key;
    switch (key) {
      case "idle":
        this.root.innerHTML = `<button type="button" class="booth-btn" data-action="open">🎙 go live</button>`;
        break;
      case "choose":
        this.root.innerHTML = `
          <span class="booth-label">stream from</span>
          <button type="button" class="booth-btn" data-action="mic">🎤 mic</button>
          <button type="button" class="booth-btn" data-action="tab">🖥 tab audio</button>
          <button type="button" class="booth-btn subtle" data-action="cancel">cancel</button>`;
        break;
      case "connecting":
        this.root.innerHTML = `<span class="booth-label">taking the booth…</span>`;
        break;
      case "live":
        this.root.innerHTML = `
          <span class="booth-onair">⏺ ON AIR</span>
          <span class="booth-label">stream muted for you</span>
          <button type="button" class="booth-btn" data-action="stop">end set</button>`;
        break;
      case "live-silent":
        this.root.innerHTML = `
          <span class="booth-onair">⏺ ON AIR</span>
          <span class="booth-label booth-error">⚠ your source is silent — check your tab/mic</span>
          <button type="button" class="booth-btn" data-action="stop">end set</button>`;
        break;
      case "occupied":
        this.root.innerHTML = `<span class="booth-label">the booth is live</span>`;
        break;
      case "error":
        this.root.innerHTML = `
          <span class="booth-label booth-error">${escapeHtml(this.broadcaster.errorText)}</span>
          <button type="button" class="booth-btn subtle" data-action="dismiss">ok</button>`;
        break;
    }
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}
